import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { banService } from '../../services/banService';
import type { IpBan } from '../../types/ban';
import BanFormModal from './components/BanFormModal';

export default function BanListPage() {
  const { t } = useTranslation('bans');
  const [showModal, setShowModal] = useState(false);
  const [editingBan, setEditingBan] = useState<IpBan | null>(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const queryClient = useQueryClient();

  // Fetch bans
  const { data: bansData, isLoading } = useQuery({
    queryKey: ['bans', 'ips', { search, page: currentPage }],
    queryFn: () => banService.getIpBans({ search, page: currentPage }),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: banService.createIpBan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bans', 'ips'] });
      toast.success(t('toast.created'));
      setShowModal(false);
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.message ||
        error.response?.data?.errors?.ip_address?.[0] ||
        t('toast.create_error');
      toast.error(message);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      banService.updateIpBan(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bans', 'ips'] });
      toast.success(t('toast.updated'));
      setEditingBan(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || t('toast.update_error'));
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: banService.deleteIpBan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bans', 'ips'] });
      toast.success(t('toast.deleted'));
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || t('toast.delete_error'));
    },
  });

  const handleDelete = (id: number) => {
    if (window.confirm(t('confirm.delete'))) {
      deleteMutation.mutate(id);
    }
  };

  const handleToggleStatus = (ban: IpBan) => {
    updateMutation.mutate({
      id: ban.id,
      data: { is_active: !ban.is_active },
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('page.title')}</h1>
          <p className="mt-1 text-sm text-gray-600">{t('page.description')}</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn btn-primary whitespace-nowrap"
        >
          {t('actions.add')}
        </button>
      </div>

      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <input
          type="text"
          placeholder={t('search.placeholder')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
          className="input flex-1 max-w-md"
        />
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">{t('loading')}</p>
          </div>
        ) : !bansData?.data || bansData.data.length === 0 ? (
          <div className="p-8 text-center">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <svg
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                />
              </svg>
            </div>
            <p className="mt-2 text-gray-500">{t('empty.no_bans')}</p>
            <p className="text-sm text-gray-400">
              {search ? t('empty.search_hint') : t('empty.first_ban_hint')}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium text-gray-700">
                      {t('table.ip_address')}
                    </th>
                    <th className="text-left p-4 font-medium text-gray-700">
                      {t('table.reason')}
                    </th>
                    <th className="text-left p-4 font-medium text-gray-700">
                      {t('table.status')}
                    </th>
                    <th className="text-left p-4 font-medium text-gray-700">
                      {t('table.banned_at')}
                    </th>
                    <th className="text-right p-4 font-medium text-gray-700">
                      {t('table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {bansData.data.map((ban) => (
                    <tr
                      key={ban.id}
                      className="border-b hover:bg-gray-50 transition"
                    >
                      <td className="p-4">
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                          {ban.ip_address}
                        </code>
                      </td>
                      <td className="p-4 text-gray-700">
                        {ban.reason || (
                          <span className="text-gray-400 italic">
                            {t('no_reason')}
                          </span>
                        )}
                      </td>
                      <td className="p-4">
                        <button
                          onClick={() => handleToggleStatus(ban)}
                          className={`px-2 py-1 rounded text-xs font-medium transition ${
                            ban.is_active
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                          }`}
                        >
                          {ban.is_active ? t('status.active') : t('status.inactive')}
                        </button>
                      </td>
                      <td className="p-4 text-gray-600 text-sm">
                        {new Date(ban.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </td>
                      <td className="p-4 text-right space-x-2">
                        <button
                          onClick={() => setEditingBan(ban)}
                          className="btn btn-secondary btn-sm"
                        >
                          {t('actions.edit')}
                        </button>
                        <button
                          onClick={() => handleDelete(ban.id)}
                          className="btn btn-danger btn-sm"
                          disabled={deleteMutation.isPending}
                        >
                          {t('actions.delete')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {bansData.last_page > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <div className="text-sm text-gray-600">
                  {t('pagination.showing', {
                    from: bansData.from,
                    to: bansData.to,
                    total: bansData.total,
                  })}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="btn btn-secondary btn-sm disabled:opacity-50"
                  >
                    {t('pagination.previous')}
                  </button>
                  <button
                    onClick={() =>
                      setCurrentPage((p) =>
                        Math.min(bansData.last_page, p + 1)
                      )
                    }
                    disabled={currentPage === bansData.last_page}
                    className="btn btn-secondary btn-sm disabled:opacity-50"
                  >
                    {t('pagination.next')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal for Add/Edit */}
      {(showModal || editingBan) && (
        <BanFormModal
          ban={editingBan}
          onClose={() => {
            setShowModal(false);
            setEditingBan(null);
          }}
          onSubmit={(data) => {
            if (editingBan) {
              updateMutation.mutate({ id: editingBan.id, data });
            } else {
              createMutation.mutate(data);
            }
          }}
          isSubmitting={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </div>
  );
}
